import { createBrowserRouter, useRouteError } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { AppShell } from '@/components/layout/AppShell'
import { Skeleton } from '@/components/ui/skeleton'

// Primary pages (lazy loaded for bundle splitting)
const Dashboard = lazy(() => import('@/pages/Dashboard').then((m) => ({ default: m.Dashboard })))
const Leads = lazy(() => import('@/pages/Leads').then((m) => ({ default: m.Leads })))
const LeadDetailPage = lazy(() => import('@/pages/LeadDetailPage').then((m) => ({ default: m.LeadDetailPage })))
const ICPs = lazy(() => import('@/pages/ICPs').then((m) => ({ default: m.ICPs })))
const Sequences = lazy(() => import('@/pages/Sequences').then((m) => ({ default: m.Sequences })))
const SequenceDetail = lazy(() => import('@/pages/SequenceDetail').then((m) => ({ default: m.SequenceDetail })))

// Secondary pages
import { Imports } from '@/pages/Imports'
import { ScrapeJobs } from '@/pages/ScrapeJobs'
import { OptOuts } from '@/pages/OptOuts'
import { Settings } from '@/pages/Settings'
import { ExtensionTokens } from '@/pages/ExtensionTokens'
import { Analytics } from '@/pages/Analytics'

// CRM pages (Phase 1C)
const TasksPage = lazy(() => import('@/pages/TasksPage').then((m) => ({ default: m.TasksPage })))
const UsersManagement = lazy(() =>
  import('@/pages/UsersManagement').then((m) => ({ default: m.UsersManagement })),
)
const UserSettings = lazy(() =>
  import('@/pages/UserSettings').then((m) => ({ default: m.UserSettings })),
)
const AdminPinSetup = lazy(() =>
  import('@/pages/AdminPinSetup').then((m) => ({ default: m.AdminPinSetup })),
)

// CRM pages (Phase 2B — Proposals & Templates)
const ProposalEditor = lazy(() =>
  import('@/pages/ProposalEditor').then((m) => ({ default: m.ProposalEditor })),
)
const ProposalTemplatesList = lazy(() =>
  import('@/pages/ProposalTemplatesList').then((m) => ({ default: m.ProposalTemplatesList })),
)
const TemplateEditor = lazy(() =>
  import('@/pages/TemplateEditor').then((m) => ({ default: m.TemplateEditor })),
)
const TemplatePreview = lazy(() =>
  import('@/pages/TemplatePreview').then((m) => ({ default: m.TemplatePreview })),
)
const SectionTemplatesManager = lazy(() =>
  import('@/pages/SectionTemplatesManager').then((m) => ({ default: m.SectionTemplatesManager })),
)

// CRM pages (Phase 3B — Contracts, Payments, Invoices, Billing)
const ContractsList = lazy(() =>
  import('@/pages/ContractsList').then((m) => ({ default: m.ContractsList })),
)
const ContractDetail = lazy(() =>
  import('@/pages/ContractDetail').then((m) => ({ default: m.ContractDetail })),
)
const PaymentsList = lazy(() =>
  import('@/pages/PaymentsList').then((m) => ({ default: m.PaymentsList })),
)
const InvoicesList = lazy(() =>
  import('@/pages/InvoicesList').then((m) => ({ default: m.InvoicesList })),
)
const BillingSettings = lazy(() =>
  import('@/pages/BillingSettings').then((m) => ({ default: m.BillingSettings })),
)

// CRM pages (Phase 3C — Intelligence + Goals)
const IntelligenceDashboard = lazy(() =>
  import('@/pages/IntelligenceDashboard').then((m) => ({ default: m.IntelligenceDashboard })),
)
const GoalsPage = lazy(() =>
  import('@/pages/GoalsPage').then((m) => ({ default: m.GoalsPage })),
)

// CRM pages (Phase 4C — Bulk Send)
const BulkSendPage = lazy(() =>
  import('@/pages/BulkSendPage').then((m) => ({ default: m.BulkSendPage })),
)
const BulkSendHistory = lazy(() =>
  import('@/pages/BulkSendHistory').then((m) => ({ default: m.BulkSendHistory })),
)
const BulkSendDetail = lazy(() =>
  import('@/pages/BulkSendDetail').then((m) => ({ default: m.BulkSendDetail })),
)

// CRM pages (Phase 2C — Products + Proposal list/view/public)
const ProductsManagement = lazy(() =>
  import('@/pages/ProductsManagement').then((m) => ({ default: m.ProductsManagement })),
)
const ProposalsList = lazy(() =>
  import('@/pages/ProposalsList').then((m) => ({ default: m.ProposalsList })),
)
const ProposalView = lazy(() =>
  import('@/pages/ProposalView').then((m) => ({ default: m.ProposalView })),
)
const BulkProposalSender = lazy(() =>
  import('@/pages/BulkProposalSender').then((m) => ({ default: m.BulkProposalSender })),
)
const PublicProposal = lazy(() =>
  import('@/pages/PublicProposal').then((m) => ({ default: m.PublicProposal })),
)

// CRM pages (Phase 5B — Operations: Events, Support, Backlog)
const EventsCalendar = lazy(() =>
  import('@/pages/EventsCalendar').then((m) => ({ default: m.EventsCalendar })),
)
const SupportRequests = lazy(() =>
  import('@/pages/SupportRequests').then((m) => ({ default: m.SupportRequests })),
)
const SupportRequestDetail = lazy(() =>
  import('@/pages/SupportRequestDetail').then((m) => ({ default: m.SupportRequestDetail })),
)
const BacklogManagement = lazy(() =>
  import('@/pages/BacklogManagement').then((m) => ({ default: m.BacklogManagement })),
)

// CRM pages (Phase 5C — Upload portals + CRM data import)
const CustomerUploadPortal = lazy(() =>
  import('@/pages/CustomerUploadPortal').then((m) => ({ default: m.CustomerUploadPortal })),
)
const SpeakerUploadPortal = lazy(() =>
  import('@/pages/SpeakerUploadPortal').then((m) => ({ default: m.SpeakerUploadPortal })),
)
const UploadPortalManager = lazy(() =>
  import('@/pages/UploadPortalManager').then((m) => ({ default: m.UploadPortalManager })),
)
const CRMImportsList = lazy(() =>
  import('@/pages/CRMImportsList').then((m) => ({ default: m.CRMImportsList })),
)
const CRMImportDetail = lazy(() =>
  import('@/pages/CRMImportDetail').then((m) => ({ default: m.CRMImportDetail })),
)

// CRM pages (Phase 4B — Email)
const BrevoSettings = lazy(() =>
  import('@/pages/BrevoSettings').then((m) => ({ default: m.BrevoSettings })),
)
const GmailSettings = lazy(() =>
  import('@/pages/GmailSettings').then((m) => ({ default: m.GmailSettings })),
)
const SystemEmailTemplates = lazy(() =>
  import('@/pages/SystemEmailTemplates').then((m) => ({ default: m.SystemEmailTemplates })),
)
const SystemEmailEditor = lazy(() =>
  import('@/pages/SystemEmailEditor').then((m) => ({ default: m.SystemEmailEditor })),
)
const SyncedEmailsList = lazy(() =>
  import('@/pages/SyncedEmailsList').then((m) => ({ default: m.SyncedEmailsList })),
)

// CRM pages (Emergent-match sidebar — orphaned pages wired up)
const BoardView = lazy(() =>
  import('@/pages/BoardView').then((m) => ({ default: m.BoardView })),
)
const MemberTrackerBoard = lazy(() =>
  import('@/pages/MemberTrackerBoard').then((m) => ({ default: m.MemberTrackerBoard })),
)
const MediaProductionBoards = lazy(() =>
  import('@/pages/MediaProductionBoards').then((m) => ({ default: m.MediaProductionBoards })),
)
const ContactsList = lazy(() =>
  import('@/pages/ContactsList').then((m) => ({ default: m.ContactsList })),
)
const NurturingList = lazy(() =>
  import('@/pages/NurturingList').then((m) => ({ default: m.NurturingList })),
)

function PageFallback() {
  return (
    <div className="space-y-4 p-2">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-64 w-full" />
    </div>
  )
}

const withSuspense = (node: React.ReactNode) => <Suspense fallback={<PageFallback />}>{node}</Suspense>

function RouteErrorBoundary() {
  // react-router's useRouteError gives us the actual thrown error
  const routeError = useRouteError() as unknown
  // Fallback: global capture from main.tsx for window-level errors
  const winError =
    typeof window !== 'undefined'
      ? (window as unknown as { __lastError?: Error }).__lastError
      : undefined
  const err = (routeError as Error) ?? winError
  const msg = String((err as { message?: string })?.message ?? err ?? '')

  // Chunk-load auto-reload (stale bundle after redeploy)
  if (typeof window !== 'undefined') {
    const isChunkError =
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('ChunkLoadError') ||
      msg.includes('Unable to preload CSS')
    if (isChunkError && !sessionStorage.getItem('__reloaded_once')) {
      sessionStorage.setItem('__reloaded_once', '1')
      window.location.reload()
    } else {
      sessionStorage.removeItem('__reloaded_once')
    }
  }

  const status = (routeError as { status?: number })?.status
  const statusText = (routeError as { statusText?: string })?.statusText
  const url = typeof window !== 'undefined' ? window.location.pathname : ''

  return (
    <div className="mx-auto max-w-xl rounded-lg border border-border bg-card p-8">
      <p className="text-lg font-semibold">Something went wrong</p>
      <p className="mt-1 text-xs text-muted-foreground">at <code>{url}</code></p>
      <div className="mt-4 rounded-md border border-border bg-muted/30 p-3 text-xs font-mono">
        {status && <p className="font-semibold">HTTP {status} {statusText}</p>}
        <p className="whitespace-pre-wrap break-words">{msg || 'Unknown error'}</p>
      </div>
      <p className="mt-4 text-sm text-muted-foreground">
        Try reloading. If it keeps happening, share this message so it can be fixed.
      </p>
      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            sessionStorage.removeItem('__reloaded_once')
            window.location.reload()
          }}
          className="rounded-md border border-border px-4 py-2 text-sm font-medium"
        >
          Reload
        </button>
        <a
          href="/"
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        >
          Back to dashboard
        </a>
      </div>
    </div>
  )
}

export const router = createBrowserRouter([
  // ── Public routes (no AppShell, no CF Access cookie) ─────────────────────
  {
    path: '/p/:token',
    id: 'public-proposal',
    element: withSuspense(<PublicProposal />),
  },
  // Phase 5C — upload portals (unauthenticated, token-gated)
  {
    path: '/upload/:token',
    id: 'customer-upload-portal',
    element: withSuspense(<CustomerUploadPortal />),
  },
  {
    path: '/upload-speaker/:token',
    id: 'speaker-upload-portal',
    element: withSuspense(<SpeakerUploadPortal />),
  },

  // ── Admin routes (behind CF Access via AppShell) ──────────────────────────
  {
    path: '/',
    element: <AppShell />,
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, id: 'dashboard', element: withSuspense(<Dashboard />) },
      { path: 'leads', id: 'leads', element: withSuspense(<Leads />) },
      { path: 'leads/:id', id: 'lead-detail', element: withSuspense(<LeadDetailPage />) },
      { path: 'icps', id: 'icps', element: withSuspense(<ICPs />) },
      { path: 'sequences', id: 'sequences', element: withSuspense(<Sequences />) },
      { path: 'sequences/:id', id: 'sequence-detail', element: withSuspense(<SequenceDetail />) },
      { path: 'imports', id: 'imports', element: <Imports /> },
      { path: 'scrape-jobs', id: 'scrape-jobs', element: <ScrapeJobs /> },
      { path: 'opt-outs', id: 'opt-outs', element: <OptOuts /> },
      { path: 'settings', id: 'settings', element: <Settings /> },
      { path: 'extension-tokens', id: 'extension-tokens', element: <ExtensionTokens /> },
      { path: 'analytics', id: 'analytics', element: <Analytics /> },
      // CRM — Phase 1C
      { path: 'tasks', id: 'tasks', element: withSuspense(<TasksPage />) },
      { path: 'users', id: 'users', element: withSuspense(<UsersManagement />) },
      { path: 'settings/user', id: 'user-settings', element: withSuspense(<UserSettings />) },
      { path: 'admin-pin', id: 'admin-pin', element: withSuspense(<AdminPinSetup />) },
      // CRM — Phase 2B: Proposal authoring
      { path: 'proposals/new', id: 'proposal-new', element: withSuspense(<ProposalEditor />) },
      { path: 'proposals/:id/edit', id: 'proposal-edit', element: withSuspense(<ProposalEditor />) },
      // CRM — Phase 2B: Template management
      { path: 'proposal-templates', id: 'proposal-templates', element: withSuspense(<ProposalTemplatesList />) },
      { path: 'templates/:id/edit', id: 'template-edit', element: withSuspense(<TemplateEditor />) },
      { path: 'templates/:id/preview', id: 'template-preview', element: withSuspense(<TemplatePreview />) },
      // CRM — Phase 2B: Section library
      { path: 'templates', id: 'templates', element: withSuspense(<SectionTemplatesManager />) },
      // CRM — Phase 3B: Contracts, Payments, Invoices, Billing
      { path: 'contracts', id: 'contracts', element: withSuspense(<ContractsList />) },
      { path: 'contracts/:id', id: 'contract-detail', element: withSuspense(<ContractDetail />) },
      { path: 'payments', id: 'payments', element: withSuspense(<PaymentsList />) },
      { path: 'invoices', id: 'invoices', element: withSuspense(<InvoicesList />) },
      { path: 'settings/billing', id: 'billing-settings', element: withSuspense(<BillingSettings />) },
      // CRM — Phase 3C: Intelligence + Goals
      { path: 'intelligence', id: 'intelligence', element: withSuspense(<IntelligenceDashboard />) },
      { path: 'goals', id: 'goals', element: withSuspense(<GoalsPage />) },
      // CRM — Phase 4C: Bulk Send (system-email variant; /proposals/bulk-send remains the proposal variant)
      { path: 'bulk-send', id: 'bulk-send-launch', element: withSuspense(<BulkSendPage />) },
      { path: 'bulk-send/history', id: 'bulk-send-history', element: withSuspense(<BulkSendHistory />) },
      { path: 'bulk-send/:id', id: 'bulk-send-detail', element: withSuspense(<BulkSendDetail />) },
      // CRM — Phase 2C: Products + Proposals list/view + bulk
      { path: 'products', id: 'products', element: withSuspense(<ProductsManagement />) },
      { path: 'proposals', id: 'proposals', element: withSuspense(<ProposalsList />) },
      { path: 'proposals/bulk-send', id: 'bulk-send', element: withSuspense(<BulkProposalSender />) },
      { path: 'proposals/:id', id: 'proposal-view', element: withSuspense(<ProposalView />) },
      // CRM — Phase 5B: Operations — Events, Support, Backlog
      { path: 'events', id: 'events-calendar', element: withSuspense(<EventsCalendar />) },
      { path: 'support', id: 'support-requests', element: withSuspense(<SupportRequests />) },
      { path: 'support/:id', id: 'support-request-detail', element: withSuspense(<SupportRequestDetail />) },
      { path: 'backlog', id: 'backlog', element: withSuspense(<BacklogManagement />) },
      // CRM — Phase 4B: Email
      { path: 'settings/brevo', id: 'brevo-settings', element: withSuspense(<BrevoSettings />) },
      { path: 'settings/gmail', id: 'gmail-settings', element: withSuspense(<GmailSettings />) },
      { path: 'system-emails', id: 'system-emails', element: withSuspense(<SystemEmailTemplates />) },
      { path: 'system-emails/:id/edit', id: 'system-email-editor', element: withSuspense(<SystemEmailEditor />) },
      { path: 'synced-emails', id: 'synced-emails', element: withSuspense(<SyncedEmailsList />) },
      // CRM — Phase 5C: Upload portals + CRM data import
      { path: 'settings/upload-portals', id: 'upload-portal-manager', element: withSuspense(<UploadPortalManager />) },
      { path: 'crm-imports', id: 'crm-imports', element: withSuspense(<CRMImportsList />) },
      { path: 'crm-imports/:id', id: 'crm-import-detail', element: withSuspense(<CRMImportDetail />) },
      // Sidebar Emergent-match routes (previously orphaned)
      { path: 'boards', id: 'boards', element: withSuspense(<MemberTrackerBoard />) },
      { path: 'boards/:id', id: 'board-view', element: withSuspense(<BoardView />) },
      { path: 'contacts', id: 'contacts', element: withSuspense(<ContactsList />) },
      { path: 'nurturing', id: 'nurturing', element: withSuspense(<NurturingList />) },
      { path: 'media-boards', id: 'media-boards', element: withSuspense(<MediaProductionBoards />) },
      // Phase 6 backend-complete pages — minimal placeholder routes until UI pages ship
      { path: 'press-releases', id: 'press-releases', element: <div className="text-muted-foreground text-sm">Press Releases UI pending — backend live at /api/v1/admin/press-releases</div> },
      { path: 'contact-submissions', id: 'contact-submissions', element: <div className="text-muted-foreground text-sm">Contact Submissions UI pending — backend live at /api/v1/admin/contact-submissions</div> },
      { path: 'docs', id: 'docs', element: <div className="text-muted-foreground text-sm">Documentation page pending.</div> },
    ],
  },
])
